"use client";
import { useCallback, useState, Fragment, useContext, useMemo } from "react";
import { useSelector } from "react-redux";

import classNames from "classnames";

import {
  Accordion,
  AccordionToggle,
  AccordionCollapse,
  AccordionContext,
} from "@stadium/ui/src/components/accordion/bootstrap-accordion/styled";
import RadioInput from "@stadium/ui/src/components/form/RadioInput";
import CheckboxInput from "@stadium/ui/src/components/form/CheckboxInput";
import Label from "@stadium/ui/src/components/form/Label";
import RangeInput from "@stadium/shared/src/components/filter/range-input";
import Button, { LinkButton } from "@stadium/shared/src/components/shared/button";
import { StyledLink } from "@stadium/ui/src/components/button/styled-button";
import SearchInput from "@stadium/shared/src/components/filter/search-input";

import { useFilter } from "@stadium/shared/src/hooks/use-filter";
import { scrollProductListingHeaderIntoView } from "@stadium/shared/src/helpers/product-listing-scroll";

import { selectAppliedFiltersCount } from "@stadium/redux-utils/selectors/filterSelector";

import { useWithAnalytics } from "@stadium/services/analytics/hooks/use-with-analytics";
import {
  ANALYTICS_EVENT_ACTION_APPLIED,
  ANALYTICS_EVENT_ACTION_REMOVED,
} from "@stadium/services/analytics/constants/default";

import { FilterStateObj } from "@stadium/redux-utils/slices/filter.slice";

import { CATALOG_REQUEST_ITEM_TYPE_FORM_LINK } from "@stadium/shared/src/constants/stores/routes";

import styles from "./main.module.scss";

import {
  FilterType,
  FilterDisplayType,
  FilterConfigOption,
  FilterConfigItem,
  NestedFilterType,
} from "./types";
import {
  checkIsApplying,
  getFilterCountOffset,
  findOptionByValue,
  shouldDisableClearAllForPreselect,
} from "./helpers";

// ─── Constants ───────────────────────────────────────────────────────

const DEFAULT_SEARCH_ENABLING_ROW_LIMIT = 8;
const DEFAULT_VIEW_ALL_ENABLING_ROW_LIMIT = 8;
const DEFAULT_COLUMNS = 2;
const DEFAULT_ARR = [];

// ─── Types ───────────────────────────────────────────────────────────

type FilterWrapperProps = {
  config: FilterConfigItem[];
  isResponsiveView?: boolean;
  onViewAll?: ({
    filterItemId,
    parentItemId,
    setShowAll,
  }: {
    filterItemId?: string;
    parentItemId?: string;
    setShowAll?: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  }) => void;
  showClearAllBtn?: boolean;
  disableClearAllBtn?: boolean;
  onFilterChange?: (type: FilterType, args: any, data: FilterStateObj) => void;
  eventLocationAnalytics?: {
    track?: boolean;
    target?: string;
    action?: string;
    eventProperties?: Record<string, any>;
  };
};

type FilterProps = {
  data: FilterStateObj;
  onChange: (type: FilterType, args: any) => void;
  onClear: () => void;
} & FilterWrapperProps;

type OptionRenderProps = {
  option: FilterConfigOption;
  item: FilterConfigItem;
  parentType: NestedFilterType;
  parentId: string;
  isTopLevel: boolean;
  isSelected: boolean;
  optionValue: string;
  onChange: FilterProps["onChange"];
  config: FilterConfigItem[];
  renderOptions: (
    item: FilterConfigItem,
    items: FilterConfigOption[],
    parentType: NestedFilterType,
    parentId: string,
    isTopLevel?: boolean,
    options?: {
      showSearch?: boolean;
      showViewAll?: boolean;
    },
  ) => React.ReactNode;
};

// ─── Helpers ─────────────────────────────────────────────────────────

const renderOptionAffix = (affix: unknown, className: string) => {
  if (!affix) return null;

  // If caller provided a non-primitive ReactNode (element, fragment, array, etc),
  // render it as-is. If it's a primitive, wrap for consistent styling.
  const isPrimitive = typeof affix === "string" || typeof affix === "number";
  return isPrimitive ? <span className={className}>{affix}</span> : <>{affix as any}</>;
};

/** Recursively checks if any option (or its children) is selected. */
const hasAnySelection = (
  options: FilterConfigOption[],
  getIsValueSelected: (id: string, value: string) => boolean,
): boolean =>
  options.some((opt) => {
    const childId = opt.childId || opt.id;
    if (opt.isSelected || getIsValueSelected(childId, String(opt.value ?? opt.id))) return true;
    return opt.children?.length ? hasAnySelection(opt.children, getIsValueSelected) : false;
  });

const getEffectiveLimit = (item: FilterConfigItem, baseLimit: number) =>
  item.displayType === FilterDisplayType.GRID
    ? (item.columns || DEFAULT_COLUMNS) * baseLimit
    : baseLimit;

// ─── Option Renderers ────────────────────────────────────────────────

/** LINK-based Accordion (Nested Categories) */
const LinkAccordionOption = ({
  option,
  item,
  isTopLevel,
  renderOptions,
  getIsValueSelected,
}: OptionRenderProps & {
  getIsValueSelected: (id: string, value: string) => boolean;
}) => {
  const childId = option.childId || option.id;
  const childType = option.childType || FilterType.LINK;
  const isTopLevelGroup = isTopLevel;
  const isGroupSelected = hasAnySelection([option], getIsValueSelected);
  const options = {
    showSearch: option.showSearch,
    showViewAll: option.showViewAll,
  };

  return (
    <Accordion
      key={option.id}
      eventKey={option.id}
      defaultOpen={option.children?.some((c) => c.isSelected)}
      className={classNames(styles.filterGroupHeader, {
        [styles.topLevelCategoryGroup]: isTopLevelGroup,
      })}
    >
      <AccordionToggle
        eventKey={option.id}
        baseClass={classNames(styles.groupHeaderBtn, {
          [styles.topLevelCategoryBtn]: isTopLevelGroup,
        })}
        showArrow={false}
      >
        <span className={styles.radioLabelWrapper}>
          {renderOptionAffix(option.prefix, styles.labelPrefix)}
          <span
            className={classNames(styles.radioLabelValue, {
              [styles.selected]: isGroupSelected,
              [styles.topLevelLabel]: isTopLevelGroup,
            })}
          >
            {option.label}
          </span>
          {renderOptionAffix(option.suffix, styles.labelSuffix)}
        </span>
        <ChevronIcon eventKey={option.id} className={styles.itemChevron} />
      </AccordionToggle>
      <AccordionCollapse eventKey={option.id}>
        <div
          className={classNames(styles.nestedItems, {
            [styles.nestedCategoryItems]: isTopLevelGroup,
          })}
        >
          {renderOptions(item, option.children, childType, childId, false, options)}
        </div>
      </AccordionCollapse>
    </Accordion>
  );
};

/** GRID-based Buttons */
const GridButtonOption = ({
  option,
  parentId,
  isSelected,
  optionValue,
  onChange,
  config,
}: OptionRenderProps) => (
  <Button
    styleType="no-style"
    className={classNames(styles.sizeBox, { [styles.selected]: isSelected })}
    onClick={(e) => {
      onChange(FilterType.MULTI_SELECT, {
        id: parentId,
        value: optionValue,
        checked: !isSelected,
        config,
        _e: e,
      });
    }}
    type="button"
  >
    <span className={styles.radioLabelWrapper}>
      {renderOptionAffix(option.prefix, styles.labelPrefix)}
      <span className={styles.radioLabelValue}>{option.label}</span>
      {renderOptionAffix(option.suffix, styles.labelSuffix)}
    </span>
  </Button>
);

/** LINK-based Labels (Plain Links) */
const LinkOption = ({ option, isTopLevel, isSelected }: OptionRenderProps) => (
  <div
    className={classNames(styles.filterListItemBlock, {
      [styles.topLevelCategoryLinkBlock]: isTopLevel,
    })}
  >
    <StyledLink
      href={option.href}
      className={classNames({
        [styles.topLevelCategoryLink]: isTopLevel,
        [styles.nestedLink]: !isTopLevel,
      })}
    >
      <span className={styles.radioLabelWrapper}>
        {renderOptionAffix(option.prefix, styles.labelPrefix)}
        <span
          className={classNames(styles.radioLabelValue, {
            [styles.topLevelLabel]: isTopLevel,
            [styles.selected]: isSelected,
          })}
        >
          {option.label}
        </span>
        {renderOptionAffix(option.suffix, styles.labelSuffix)}
      </span>
    </StyledLink>
  </div>
);

/** Checkbox / Radio Inputs (Standard Tree) */
const InputOption = ({
  option,
  item,
  parentType,
  parentId,
  isSelected,
  optionValue,
  onChange,
  config,
  renderOptions,
}: OptionRenderProps) => {
  const isRadio = parentType === FilterType.SINGLE_SELECT;
  const InputComponent = isRadio ? RadioInput : CheckboxInput;
  const hasChildren = option.children && option.children.length > 0;
  const childType = option.childType || parentType;
  const childId = option.childId || option.id;
  const options = {
    showSearch: option.showSearch,
    showViewAll: option.showViewAll,
  };

  return (
    <>
      <div className={styles.filterListItemBlock}>
        <InputComponent
          id={`${parentType}-${option.id}`}
          name={isRadio ? parentId : option.id}
          value={optionValue}
          checked={isSelected}
          classes={styles.filterRadioItem}
          onChange={(e) => {
            onChange(isRadio ? FilterType.SINGLE_SELECT : FilterType.MULTI_SELECT, {
              ...e,
              id: parentId,
              config,
            });
          }}
          label={
            <Label iconType={isRadio ? "radioOutline" : undefined} styles={styles}>
              <span className={styles.radioLabelWrapper}>
                {renderOptionAffix(option.prefix, styles.labelPrefix)}
                <span className={styles.radioLabelValue}>{option.label}</span>
                {renderOptionAffix(option.suffix, styles.labelSuffix)}
              </span>
            </Label>
          }
        />
      </div>
      {isSelected && hasChildren && (
        <div className={styles.nestedItems}>
          {renderOptions(item, option.children, childType, childId, false, options)}
        </div>
      )}
    </>
  );
};

// ─── Default Handlers ────────────────────────────────────────────────

const defaultOnViewAll: FilterWrapperProps["onViewAll"] = ({ parentItemId, setShowAll }) => {
  setShowAll((prev) => ({
    ...prev,
    [parentItemId]: !prev[parentItemId],
  }));
};

// ─── Ask An Expert ───────────────────────────────────────────────────

const AskAnExpertHelp = () => (
  <div className={styles.buttonsBlock}>
    <LinkButton
      href={CATALOG_REQUEST_ITEM_TYPE_FORM_LINK}
      styleType="primary"
      className={styles.requestLink}
      target="_blank"
    >
      <span className={`icon-message-square ${styles.requestLinkIcon}`} /> Ask An Expert
    </LinkButton>
    <p className={styles.rushHelpText}>
      <span className={styles.rushHelpTextBold}>Need it fast?</span> Ask about rush production and
      shipping.
    </p>
  </div>
);

function ChevronIcon({
  eventKey,
  className,
}: {
  readonly eventKey: string;
  readonly className?: string;
}) {
  const currentEventKey = useContext(AccordionContext);
  const isOpen = currentEventKey === eventKey;
  return <span className={classNames(className, { [styles.chevronCollapsed]: !isOpen })} />;
}

const Filter = ({
  config,
  data,
  onChange,
  onClear,
  isResponsiveView,
  onViewAll = defaultOnViewAll,
  showClearAllBtn,
  disableClearAllBtn,
}: FilterProps) => {
  const [showMoreGroups, setShowMoreGroups] = useState<Record<string, boolean>>({});
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>({});

  const getSelectedValues = useCallback(
    (id: string): string[] => {
      const val = data[id];
      if (Array.isArray(val)) return val as string[];
      if (typeof val === "string" && val) return [val];
      return DEFAULT_ARR;
    },
    [data],
  );

  const getIsValueSelected = useCallback(
    (id: string, value: string): boolean => getSelectedValues(id).includes(value),
    [getSelectedValues],
  );

  const getIsFilterActive = (item: FilterConfigItem) => {
    if (item.defaultOpen) return true;
    if (item.type === FilterType.RANGE) {
      const val = data[item.id];
      return !!val && (val.min !== undefined || val.max !== undefined);
    }
    if (getSelectedValues(item.id).length > 0) return true;
    return Array.isArray(item.options) && hasAnySelection(item.options, getIsValueSelected);
  };

  // ─── Render Helpers ────────────────────────────────────────────────
  const renderFilterOption = (
    option: FilterConfigOption,
    item: FilterConfigItem,
    parentType: NestedFilterType,
    parentId: string,
    isTopLevel: boolean,
  ) => {
    const optionValue = String(option.value ?? option.id);
    const hasChildren = option.children && option.children.length > 0;
    const isSelected = option.isSelected || getIsValueSelected(parentId, optionValue);

    const props: OptionRenderProps = {
      option,
      item,
      parentType,
      parentId,
      isTopLevel,
      isSelected,
      optionValue,
      onChange,
      config,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      renderOptions: renderFilterOptions,
    };

    if (hasChildren && parentType === FilterType.LINK) {
      return <LinkAccordionOption {...props} getIsValueSelected={getIsValueSelected} />;
    }
    if (item.displayType === FilterDisplayType.GRID) return <GridButtonOption {...props} />;
    if (parentType === FilterType.LINK) return <LinkOption {...props} />;
    return <InputOption {...props} />;
  };

  const renderFilterOptions = (
    item: FilterConfigItem,
    items: FilterConfigOption[],
    parentType: NestedFilterType,
    parentId: string,
    isTopLevel: boolean = true,
    options?: {
      showSearch?: boolean;
      showViewAll?: boolean;
    },
  ) => {
    const isShowingMore = showMoreGroups[parentId];
    const showSearch =
      options?.showSearch ??
      items.length > getEffectiveLimit(item, DEFAULT_SEARCH_ENABLING_ROW_LIMIT);

    const query = searchQueries[parentId] || "";
    const currentItems =
      showSearch && query
        ? items.filter((opt) => opt.label.toString().toLowerCase().includes(query.toLowerCase()))
        : items;

    const showViewAll = options?.showViewAll ?? true;
    const viewAllLimit =
      item.viewAllLimit ?? getEffectiveLimit(item, DEFAULT_VIEW_ALL_ENABLING_ROW_LIMIT);
    const shouldTruncate = showViewAll && currentItems.length > viewAllLimit;
    const visibleItems =
      shouldTruncate && !isShowingMore ? currentItems.slice(0, viewAllLimit) : currentItems;

    const isGrid = item.displayType === FilterDisplayType.GRID;

    return (
      <div
        className={classNames(isGrid ? styles.sizeGrid : styles.filterListItems)}
        style={
          isGrid ? { gridTemplateColumns: `repeat(${item.columns || DEFAULT_COLUMNS}, 1fr)` } : {}
        }
      >
        {showSearch && (
          <div className={styles.searchBarWrapper}>
            <SearchInput
              id={`search-${parentId}`}
              name={`search-${parentId}`}
              value={query}
              onChange={({ value }) => {
                setSearchQueries((prev) => ({ ...prev, [parentId]: value }));
              }}
              containerClass={styles.filterSearchBarContainer}
              className={styles.filterSearchBarInput}
              debounceWaitTime={300}
            />
          </div>
        )}

        {visibleItems.map((option) => (
          <Fragment key={option.id}>
            {renderFilterOption(option, item, parentType, parentId, isTopLevel)}
          </Fragment>
        ))}

        {shouldTruncate && (
          <Button
            styleType="link"
            className={classNames(styles.clearFiltersBtn, styles.viewAllBtn)}
            onClick={() => {
              onViewAll({
                filterItemId: item.id,
                parentItemId: parentId,
                setShowAll: setShowMoreGroups,
              });
            }}
            type="button"
          >
            {isShowingMore ? "View Less" : "View All"}
          </Button>
        )}
      </div>
    );
  };

  const renderFilterContent = (item: FilterConfigItem) => {
    if (item.type === FilterType.RANGE) {
      return (
        <RangeInput
          item={item}
          value={data[item.id]}
          onChange={(val) => onChange(FilterType.RANGE, { id: item.id, value: val })}
        />
      );
    }
    if (Array.isArray(item.options)) {
      return renderFilterOptions(item, item.options, item.type, item.id, true, {
        showSearch: item.showSearch,
        showViewAll: item.showViewAll,
      });
    }
    return null;
  };

  if (!config?.length) return null;

  return (
    <div className={styles.filterContainer}>
      {config.map((item) => (
        <Accordion
          key={item.id}
          eventKey={item.id}
          defaultOpen={getIsFilterActive(item)}
          className={styles.filterGroup}
        >
          {item.label && (
            <AccordionToggle eventKey={item.id} baseClass={styles.filterHeader} showArrow={false}>
              <span className={styles.filterLabel}>{item.label}</span>
              <ChevronIcon eventKey={item.id} className={styles.chevron} />
            </AccordionToggle>
          )}
          <AccordionCollapse eventKey={item.id}>
            <div
              className={classNames(styles.filterListBody, {
                [styles.fullHeightList]: isResponsiveView && !item.label,
              })}
            >
              {renderFilterContent(item)}
            </div>
          </AccordionCollapse>
        </Accordion>
      ))}

      {showClearAllBtn && (
        <Button
          className={styles.clearFiltersBtn}
          onClick={() => onClear()}
          styleType="link"
          disabled={disableClearAllBtn}
        >
          Clear Filters
        </Button>
      )}
    </div>
  );
};

// ─── Components ──────────────────────────────────────────────────────

const FilterWrapper: React.FC<FilterWrapperProps> = ({
  config,
  isResponsiveView,
  onViewAll,
  onFilterChange,
  eventLocationAnalytics,
}) => {
  const filter = useFilter(config);
  const appliedFiltersCount = useSelector(selectAppliedFiltersCount);

  const disableClearAllBtn = useMemo(
    () => shouldDisableClearAllForPreselect(filter.appliedFilters, config),
    [config, filter.appliedFilters],
  );

  const withAnalytics = useWithAnalytics();

  const onChange = useCallback(
    (type: FilterType, args: any) => {
      filter.onChange(type, args);
      const nextData = filter.data;

      if (eventLocationAnalytics?.track) {
        const filterId = args?.id;
        const filterValue = args?.value;
        const currentValues = nextData?.[filterId];
        const activeGroup = config.find((group) => group.id === filterId);

        const isApplying = checkIsApplying(type, filterValue, currentValues, args?.checked);
        const countOffset = getFilterCountOffset(type, isApplying, currentValues);

        const activeOption = Array.isArray(activeGroup?.options)
          ? findOptionByValue(activeGroup.options, filterValue)
          : undefined;

        withAnalytics(undefined, {
          ...eventLocationAnalytics,
          action:
            eventLocationAnalytics?.action ??
            (isApplying ? ANALYTICS_EVENT_ACTION_APPLIED : ANALYTICS_EVENT_ACTION_REMOVED),
          eventProperties: {
            ...eventLocationAnalytics?.eventProperties,
            filter_type: filterId,
            filter_type_name: activeGroup?.label,
            filter_id: filterValue,
            filter_name: activeOption?.label ?? activeOption?.name,
            filter_value: isApplying,
            active_filters_count: appliedFiltersCount + countOffset,
          },
        })();
      }

      onFilterChange?.(type, args, nextData);
      scrollProductListingHeaderIntoView();
    },
    [
      filter.onChange,
      onFilterChange,
      filter.data,
      eventLocationAnalytics,
      withAnalytics,
      config,
      appliedFiltersCount,
    ],
  );

  const onClear = useCallback(() => {
    filter.clear();

    if (eventLocationAnalytics?.track) {
      withAnalytics(undefined, {
        ...eventLocationAnalytics,
        action: ANALYTICS_EVENT_ACTION_REMOVED,
        eventProperties: {
          ...eventLocationAnalytics.eventProperties,
          filter_type: "all",
          filter_value: false,
          active_filters_count: 0,
        },
      })();
    }

    scrollProductListingHeaderIntoView();
  }, [filter.clear, eventLocationAnalytics, withAnalytics]);

  return (
    <Filter
      config={config}
      data={filter.data}
      onChange={onChange}
      onClear={onClear}
      isResponsiveView={isResponsiveView}
      onViewAll={onViewAll}
      showClearAllBtn={!!appliedFiltersCount}
      disableClearAllBtn={disableClearAllBtn}
    />
  );
};

export { Filter, AskAnExpertHelp };
export default FilterWrapper;
